export class LinkBuilder {
  static urlTypes = [
    {
      value: 'link',
      allowedPrefixes: ['https://', 'http://'],
      standardPrefix: 'https://',
      isPrefixVisible: true,
      label: 'URL',
      placeholder: 'company.com',
      validate: (input) => {
        const url = input.split('?')[0];

        return /^((https|http)?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w%.\-_~!*'();:@&=+$,?#[\]]*)*\/?$/i.test(url);
      }
    },
    {
      value: 'email',
      allowedPrefixes: ['mailto:'],
      standardPrefix: 'mailto:',
      label: 'Email',
      placeholder: 'name@email.com',
      validate: (input) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(input);
      }
    },
    {
      value: 'phone',
      allowedPrefixes: ['tel:'],
      standardPrefix: 'tel:',
      label: 'Phone',
      placeholder: '+123456789',
      validate: (input) => {
        return /^[+]?[0-9]+$/i.test(input);
      }
    }
  ];

  static getPages () {
    const pages = Array.from({ length: INKIVE.C.BOOK.num_pages }).map(
      (_, index) => (index + 1).toString()
    );

    return pages;
  }

  static getLinkForUrl (type, value) {
    const urlType = LinkBuilder.urlTypes.find(
      (urlType) => urlType.value === type
    );
    if (!urlType) return null;

    const sanitizedValue = LinkBuilder.sanitizeAndCreateLink(value);
    if (!sanitizedValue) return null;

    const prefix = urlType.allowedPrefixes.find((allowedPrefix) =>
      sanitizedValue.startsWith(allowedPrefix)
    );

    if (sanitizedValue && prefix) {
      return sanitizedValue;
    }

    return urlType.standardPrefix + sanitizedValue;
  }

  static getLinkForPage (value, totalPages = INKIVE.C.BOOK.num_pages) {
    const page = parseInt(value, 10);
    if (isNaN(page)) return null;

    const pageExists = page > 0 && page <= totalPages;
    if (!pageExists) return null;

    return `#${page}`;
  }

  static buildLink (type, value) {
    if (!type || !value) return null;

    if (type === 'anchor') {
      return LinkBuilder.getLinkForPage(value);
    }

    return LinkBuilder.getLinkForUrl(type, value);
  }

  static sanitizeAndCreateLink (input) {
    // Basic sanitation to remove any potentially harmful script tags or attributes
    const sanitizedInput = input
      .replace(/<script.*?>.*?<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/<.*?on.*?=['"].*?['"].*?>/gi, '');

    let url;

    try {
      url = new URL(sanitizedInput);
    } catch (e) {
      // If input is not a valid URL, return null or handle as needed
      return sanitizedInput;
    }

    // Ensure the protocol is one of the allowed types
    const acceptedProtocols = LinkBuilder.urlTypes
      .map((urlType) => urlType.allowedPrefixes)
      .flat();
    if (acceptedProtocols.includes(url.protocol)) {
      return url.href;
    } else {
      // Handle the case where the protocol is not allowed
      return sanitizedInput;
    }
  }

  static getLinkValue (link) {
    if (!link) return null;

    const urlType = LinkBuilder.urlTypes.find((urlType) =>
      link.startsWith(urlType.standardPrefix)
    );
    if (!urlType) {
      const startsWithHash = link.startsWith('#');
      const page = parseInt(link.replace('#', ''), 10);
      if (startsWithHash && !isNaN(page)) {
        return page.toString();
      }

      return link;
    }

    return link.replace(urlType.standardPrefix, '');
  }

  static getLinkType (link) {
    if (!link) return null;

    const urlType = LinkBuilder.urlTypes.find((urlType) =>
      link.startsWith(urlType.standardPrefix)
    );
    if (!urlType) {
      const startsWithHash = link.startsWith('#');
      const page = parseInt(link.replace('#', ''), 10);
      if (startsWithHash && !isNaN(page)) {
        return 'anchor';
      }

      return null;
    }

    return urlType.value;
  }

  static isValidInput (link, selectedType) {
    if (!link) return false;

    const urlType = this.urlTypes.find((type) => type.value === selectedType);
    if (!urlType) {
      const startsWithHash = link.startsWith('#');
      const page = parseInt(link.replace('#', ''), 10);

      return !!(startsWithHash && !isNaN(page));
    }

    return urlType.validate(link);
  }

  static changeInputType (element, selectedType) {
    const type = this.urlTypes.find(
      (urlType) => urlType.value === selectedType
    );

    element.value = '';
    element.placeholder = type?.placeholder ?? '';

    switch (selectedType) {
      case 'link':
        element.type = 'url';
        break;
      case 'email':
        element.type = 'email';
        break;
      case 'phone':
        element.type = 'tel';
        break;
      default:
        element.type = 'text';
        break;
    }
  }

  static updateApplyButtonDisable (element, isDisabled) {
    const applyButton = element
      .closest('.ui-dropdown-items')
      .querySelector('.apply');

    applyButton.classList.toggle('disabled', isDisabled);
  }

  static handleInputChange (element, selectedUrlType) {
    this.updateApplyButtonDisable(
      element,
      !this.isValidInput(element.value, selectedUrlType)
    );
  }
}
